/**
 * Date utilities for Panama timezone (America/Panama, UTC-5)
 *
 * IMPORTANT: All frontends in this project should use these utilities
 * to ensure consistent date handling in Panama timezone.
 *
 * DO NOT use:
 * - new Date().toISOString().split('T')[0] - This converts to UTC!
 * - toLocaleDateString() without timezone - Depends on user's browser settings
 *
 * USE instead:
 * - getPanamaDateString() - For YYYY-MM-DD format (API/forms)
 * - formatDateForDisplay() - For user-friendly display
 * - formatDateTimeForDisplay() - For datetime display
 */

const PANAMA_TIMEZONE = 'America/Panama';

/**
 * Get current date in Panama timezone as YYYY-MM-DD string
 * Use this for form defaults, API calls, and anywhere you need today's date
 *
 * @example
 * const today = getPanamaDateString(); // "2024-12-10"
 */
export const getPanamaDateString = (): string => {
  const now = new Date();
  // Use Intl.DateTimeFormat to get Panama timezone date
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PANAMA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(now); // Returns YYYY-MM-DD format
};

/**
 * Convert a Date object to YYYY-MM-DD string in Panama timezone
 * Use this when you have a Date object and need a string for API calls
 *
 * @example
 * const dateStr = dateToPanamaString(new Date()); // "2024-12-10"
 */
export const dateToPanamaString = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PANAMA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

/**
 * Format a date string or Date object for user display in Panama timezone
 * Returns format: "10 dic 2024" (Spanish, short month)
 *
 * @example
 * formatDateForDisplay("2024-12-10") // "10 dic 2024"
 * formatDateForDisplay(new Date()) // "10 dic 2024"
 */
export const formatDateForDisplay = (date: string | Date | null | undefined): string => {
  if (!date) return '-';

  let dateObj: Date;
  if (typeof date === 'string') {
    // Extract just the date portion to avoid timezone shift issues.
    // Whether it's "YYYY-MM-DD" or "YYYY-MM-DDT..." we only care about the date.
    const datePart = date.split('T')[0];
    const dateMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      // Parse as local date by using components (avoids UTC interpretation)
      const [, year, month, day] = dateMatch;
      dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) return '-';

  return dateObj.toLocaleDateString('es-PA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Format a datetime string or Date object for user display in Panama timezone
 * Returns format: "10 dic 2024, 2:30 PM"
 *
 * @example
 * formatDateTimeForDisplay("2024-12-10T14:30:00Z") // "10 dic 2024, 2:30 PM"
 */
export const formatDateTimeForDisplay = (date: string | Date | null | undefined): string => {
  if (!date) return '-';

  let dateObj: Date;
  if (typeof date === 'string') {
    // For datetime strings (with 'T'), treat as UTC if no timezone info
    // For pure date strings, add time component and treat as start of day in UTC
    let dateStr = date;
    if (dateStr.includes('T')) {
      // Datetime string - add Z if no timezone
      if (!dateStr.includes('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
        dateStr = dateStr + 'Z';
      }
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Pure date string (YYYY-MM-DD) - convert to datetime at noon UTC to avoid day shift
      dateStr = dateStr + 'T12:00:00Z';
    }
    dateObj = new Date(dateStr);
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) return '-';

  return dateObj.toLocaleString('es-PA', {
    timeZone: PANAMA_TIMEZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format a date for display in a short format (no year)
 * Returns format: "10 dic"
 *
 * @example
 * formatDateShort("2024-12-10") // "10 dic"
 */
export const formatDateShort = (date: string | Date | null | undefined): string => {
  if (!date) return '-';

  let dateObj: Date;
  if (typeof date === 'string') {
    // Extract just the date portion to avoid timezone shift issues.
    const datePart = date.split('T')[0];
    const dateMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) return '-';

  return dateObj.toLocaleDateString('es-PA', {
    day: 'numeric',
    month: 'short',
  });
};

/**
 * Get the start of today in Panama timezone as ISO string
 * Useful for API queries that need a datetime
 *
 * @example
 * getTodayStartISO() // "2024-12-10T00:00:00.000-05:00"
 */
export const getTodayStartISO = (): string => {
  const today = getPanamaDateString();
  return `${today}T00:00:00`;
};

/**
 * Get the end of today in Panama timezone as ISO string
 * Useful for API queries that need a datetime range
 *
 * @example
 * getTodayEndISO() // "2024-12-10T23:59:59.000-05:00"
 */
export const getTodayEndISO = (): string => {
  const today = getPanamaDateString();
  return `${today}T23:59:59`;
};

/**
 * Parse a date string and return a Date object adjusted for Panama timezone display
 * Use sparingly - prefer the format functions above
 */
export const parsePanamaDate = (dateString: string): Date => {
  // If the date string doesn't have timezone info, treat it as Panama time
  if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    return new Date(dateString + 'T00:00:00-05:00');
  }
  return new Date(dateString);
};

/**
 * Check if a date is today in Panama timezone
 *
 * @example
 * isToday(new Date()) // true
 * isToday("2024-12-10") // true if today is Dec 10
 */
export const isToday = (date: string | Date): boolean => {
  const today = getPanamaDateString();
  const compareDate = typeof date === 'string'
    ? date.split('T')[0]
    : dateToPanamaString(date);
  return today === compareDate;
};

/**
 * Get a date N days ago in Panama timezone as YYYY-MM-DD
 *
 * @example
 * getDaysAgo(7) // Date 7 days ago
 * getDaysAgo(30) // Date 30 days ago
 */
export const getDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return dateToPanamaString(date);
};

/**
 * Get the first day of current month in Panama timezone
 *
 * @example
 * getFirstDayOfMonth() // "2024-12-01"
 */
export const getFirstDayOfMonth = (): string => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PANAMA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  });
  const [year, month] = formatter.format(now).split('-');
  return `${year}-${month}-01`;
};

/**
 * Get the last day of current month in Panama timezone
 *
 * @example
 * getLastDayOfMonth() // "2024-12-31"
 */
export const getLastDayOfMonth = (): string => {
  const now = new Date();
  // Get year and month in Panama timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PANAMA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  });
  const [year, month] = formatter.format(now).split('-');
  // Last day of month = day 0 of next month
  const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
  return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
};

// Legacy alias for backward compatibility
export const getLocalDateString = getPanamaDateString;
